import * as Msal from "msal";
import { IAuthenticationProvider, AuthenticationResult, AuthConfig, AuthenticationProvider } from "./IAuthenticationProvider";
import { IAccount } from "./IAccount";

export class MSALAuthenticationProvider implements IAuthenticationProvider {
    private myMSALObj!: Msal.UserAgentApplication;
    private requestObj!: Msal.AuthenticationParameters;
    private config!: AuthConfig;
    private isMultitenantMode: boolean;
    private accessToken: string | null = null;
    private account: IAccount | null = null;
    private tenantId: string | null = null;

    constructor(isMultitenantMode: boolean = false, uiBaseUrl: string, tenantId: string | null = null) {
        this.isMultitenantMode = isMultitenantMode;
        this.setupConfiguration(uiBaseUrl, tenantId);
        this.initializeMSAL();
    }

    private setupConfiguration(uiBaseUrl: string, tenantId: string | null): void {
        if (this.isMultitenantMode) {
            this.config = {
                clientId: "610d5baa-d751-4a60-82e1-98a02e1bedb3",
                authority: tenantId == null ? "https://login.microsoftonline.com/organizations/" : `https://login.microsoftonline.com/${tenantId}/`,
                redirectUri: uiBaseUrl,
                scopes: ["api://610d5baa-d751-4a60-82e1-98a02e1bedb3/ApiAccess"],
                postLogoutRedirectUri: `${uiBaseUrl}?finalised=true`
            };
        } else {
            this.config = {
                clientId: "d3c5ed7b-901e-4d39-9e87-e456c6be3d2b",
                authority: "https://login.microsoftonline.com/9483ff9b-08db-425d-9857-96ba1fd9beed/",
                redirectUri: uiBaseUrl,
                scopes: ["api://d3c5ed7b-901e-4d39-9e87-e456c6be3d2b/ApiAccess"],
                postLogoutRedirectUri: `${uiBaseUrl}?finalised=true`
            };
        }

        this.requestObj = {
            scopes: this.config.scopes
        };
    }

    private initializeMSAL(): void {
        const msalConfig: Msal.Configuration = {
            auth: {
                clientId: this.config.clientId,
                authority: this.config.authority,
                redirectUri: this.config.redirectUri,
                postLogoutRedirectUri: this.config.postLogoutRedirectUri,
                navigateToLoginRequestUrl: true,
            },
            cache: {
                cacheLocation: "localStorage" as "localStorage",
                storeAuthStateInCookie: true
            }
        };

        console.log(`${this.isMultitenantMode ? "multi" : "single"} tenant MSAL call inside MSALAuthenticationProvider`);
        this.myMSALObj = new Msal.UserAgentApplication(msalConfig);
    }

    async initialize(): Promise<void> {
        const self = this;
        this.myMSALObj.handleRedirectCallback((error: Msal.AuthError, response: Msal.AuthResponse | undefined) => {
            if (error) {
                console.error("MSAL Redirect Error:", error);
            } else if (response) {
                if (response.account) {
                    self.account = {
                        userName: response.account.userName,
                        name: response.account.name,
                    };
                }
                self.accessToken = response.accessToken;
                self.tenantId = response.tenantId;
            }
        });
    }



    async getTokenSilent(forceRefresh: boolean = false): Promise<AuthenticationResult> {
        try {
            const idTokenResponse = await this.myMSALObj.acquireTokenSilent(this.requestObj);
            
            if (idTokenResponse.accessToken) {
                if (this.accessToken !== idTokenResponse.accessToken && this.accessToken !== null) {
                    console.log("MSAL Token has been refreshed silently.");
                }
                
                this.accessToken = idTokenResponse.accessToken;
                if (idTokenResponse.account) {
                    this.account = {
                        userName: idTokenResponse.account.userName,
                        name: idTokenResponse.account.name,
                    };
                }
                this.tenantId = idTokenResponse.tenantId;
                

                return {
                    accessToken: this.accessToken,
                    account: this.account,
                    tenantId: this.tenantId,
                    success: true
                };
            } else {
                console.log("MSAL Token could not be refreshed silently.");
                return {
                    accessToken: null,
                    account: null,
                    tenantId: null,
                    success: false,
                    error: "Token could not be refreshed silently"
                };
            }
        } catch (error: any) {
            // Upon acquireTokenSilent failure (due to consent or interaction or login required ONLY)
            if (this.requiresInteraction(error.errorCode)) {
                // Handle Office context
                if (window.localStorage.getItem("Excel") === "Excel") {
                    (window as any).Office?.context?.ui?.displayDialogAsync(
                        this.config.redirectUri,
                        { width: 30, height: 40 },
                        (result: any) => {
                            console.log('Office dialog success');
                        }
                    );
                } else {
                    this.myMSALObj.acquireTokenRedirect(this.requestObj);
                }
            }
            
            console.log("MSAL getTokenSilent error:", error);
            return {
                accessToken: null,
                account: null,
                tenantId: null,
                success: false,
                error: error.message || "Token acquisition failed"
            };
        }
    }

    async login(loginState?: string): Promise<void> {
        if (loginState) {
            localStorage.setItem("FluenceLoginState", loginState);
        }

        if (this.accessToken === null) {
            this.myMSALObj.loginRedirect(this.requestObj);
        }
    }

    async forceLogin(): Promise<void> {
        const requestObj: Msal.AuthenticationParameters = JSON.parse(JSON.stringify(this.requestObj));
        requestObj.prompt = "select_account";
        this.myMSALObj.loginRedirect(requestObj);
    }

    async logout(): Promise<void> {
        this.accessToken = null;
        this.account = null;
        this.tenantId = null;
        
        
        // Clear localStorage items
        localStorage.removeItem("tenantWebApiUrl");
        localStorage.removeItem("tenantName");
        localStorage.removeItem("FluenceLoginState");
        
        this.myMSALObj.logout();
    }



    getAccount(): IAccount | null {
        return this.account;
    }

    getAccessToken(): string | null {
        return this.accessToken;
    }

    getTenantId(): string | null {
        return this.tenantId;
    }

    getProviderType(): AuthenticationProvider {
        return AuthenticationProvider.MSAL;
    }

    private requiresInteraction(errorCode: any): boolean {
        if (!errorCode || !errorCode.length) {
            return false;
        }
        return errorCode === "consent_required" ||
            errorCode === "interaction_required" ||
            errorCode === "user_login_error" ||
            errorCode === "login_required";
    }
}
